import { CheckBadgeIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { MyVendorRiskScoreBadge } from "./MyVendorRiskScoreBadge";
import useMyVendorRequestAccess from "../../custom-hooks/useMyVendorRequestAccess";
import {
  getBadgeMetaParts,
  getMyVendorRequestDisplayTitle,
  getRequestStatusBucket,
  getRequestTypeFamily,
} from "../../utils/myVendorsRequestUtils";
import {
  classNames,
  getMyVendorRequestBadgeLockProps,
  handleMyVendorRequestBadgeClick,
} from "./myVendorRequestBadgeLock";

function isScoreEligibleRequest(request) {
  return request?.type === "RFI" && request?.isCompleted;
}

function TableRequestBadgeIcon({ type, inverted = false }) {
  const family = getRequestTypeFamily(type);
  const isVqa = family === "VQA";
  const Icon = isVqa ? CheckBadgeIcon : CheckCircleIcon;

  if (inverted) {
    const bg = isVqa ? "bg-rose-600" : "bg-indigo-600";
    return (
      <span
        className={`inline-flex flex-shrink-0 items-center justify-center rounded ${bg} p-0.5 text-white`}
      >
        <Icon className="h-3 w-3" />
      </span>
    );
  }

  const bg = isVqa ? "bg-rose-100" : "bg-indigo-100";
  const text = isVqa ? "text-rose-700" : "text-indigo-700";
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center justify-center rounded ${bg} ${text} p-0.5`}
    >
      <Icon className="h-3 w-3" />
    </span>
  );
}

/** Compact table badge — icon conveys request type; no type label in title (Ticket 1.3). */
export default function MyVendorTableRequestBadge({ request, onClick, inverted = false }) {
  const { canOpenRequest, isVqaOnly } = useMyVendorRequestAccess();
  const isLocked = !canOpenRequest(request);
  const lockProps = getMyVendorRequestBadgeLockProps(request, isLocked, { isVqaOnly });
  const titleStripped = getMyVendorRequestDisplayTitle(request);
  const meta = getBadgeMetaParts(request);
  const statusBucket = getRequestStatusBucket(request);
  const showScoreChip =
    isScoreEligibleRequest(request) &&
    request.riskScore &&
    typeof request.riskScore.score === "number";
  const defaultTitle = `${request.type || ""} · ${titleStripped || request.title || ""}`.trim();

  const renderMetaLine = () => {
    if (!meta) return null;
    const { dateStr, locationShort, prefix } = meta;
    return (
      <span className="truncate text-left text-[10px] text-gray-500">
        {prefix}
        {dateStr}
        {dateStr && locationShort ? <span> · </span> : null}
        {locationShort}
      </span>
    );
  };

  return (
    <button
      type="button"
      onClick={(event) =>
        handleMyVendorRequestBadgeClick(event, {
          disabled: isLocked,
          request,
          onClick,
        })
      }
      aria-disabled={lockProps["aria-disabled"]}
      className={classNames(
        "inline-flex max-w-full min-h-[40px] flex-col items-stretch gap-0.5 rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200 transition-colors",
        isLocked
          ? "cursor-not-allowed opacity-60"
          : "hover:bg-gray-50 hover:ring-gray-300"
      )}
      title={lockProps.title || defaultTitle}
    >
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <TableRequestBadgeIcon type={request.type} inverted={inverted} />
        <span className="min-w-0 flex-1 truncate text-xs text-gray-700">{titleStripped}</span>
        {statusBucket ? (
          <span className="inline-flex flex-shrink-0 items-center gap-1 text-[10px] text-gray-500">
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${statusBucket.dotClass}`}
              aria-hidden="true"
            />
            {statusBucket.label}
          </span>
        ) : null}
        {showScoreChip ? (
          <span className="flex-shrink-0">
            <MyVendorRiskScoreBadge
              score={request.riskScore.score}
              rating={request.riskScore.rating}
              size="xs"
              showRatingText={false}
              showPercentage
            />
          </span>
        ) : null}
      </span>
      {renderMetaLine()}
    </button>
  );
}
